import React, { useEffect, useState } from "react";
import "./Login.css";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { api_url, DEFAULT_LOGO_SRC } from "../../constant/constant";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [captchaEnabled, setCaptchaEnabled] = useState(false);
  const [SSOEnabled, setSSOEnabled] = useState(false);
  const [systemAllowed, setSystemAllowed] = useState(true);
  const [systemLanguage, setSystemLanguage] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingCircle, setCircleLoading] = useState(false);
  const [captchaImage, setCaptchaImage] = useState(null);
  const [captchaResponse, setCaptchaResponse] = useState("");
  const [captchaId, setCaptchaId] = useState(null);
  const [twoFA, setTwoFA] = useState(false);
  const [twoFaVal, setTwoFaVal] = useState("");

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const applyLanguage = (languageCode) => {
    if (!languageCode) return;

    localStorage.setItem("lng", languageCode);
    i18n.changeLanguage(languageCode);
    document.documentElement.dir = languageCode === "ar" ? "rtl" : "ltr";
    document.body.setAttribute("dir", languageCode === "ar" ? "rtl" : "ltr");
  };

  const saveSession = (data) => {
    if (data?.token) {
      localStorage.setItem("userToken", data.token);
    }

    if (data?.user) {
      localStorage.setItem("userData", JSON.stringify(data.user));
    }

    if (data?.session_lifetime) {
      localStorage.setItem("session_lifetime", data.session_lifetime);
    }

    if (data?.language_code) {
      applyLanguage(data.language_code);
    }
  };

  const applyCaptchaFromResponse = (data) => {
    if (data?.captcha) {
      setCaptchaEnabled(true);
      setCaptchaImage(data.captcha);
      setCaptchaId(data.captcha_id || null);
      setCaptchaResponse("");
      return true;
    }

    if (data?.captcha_required === false) {
      setCaptchaEnabled(false);
      setCaptchaImage(null);
      setCaptchaId(null);
      setCaptchaResponse("");
    }

    return false;
  };

  const handleRequiresAction = (data) => {
    if (data?.token) {
      localStorage.setItem("userToken", data.token);
    }

    if (data?.language_code) {
      applyLanguage(data.language_code);
    }

    applyCaptchaFromResponse(data);

    if (data?.status !== "requires_action") {
      return false;
    }

    if (data.code === "requires_tac_acceptance") {
      navigate("/Terms", { state: { terms: data.terms } });
      return true;
    }

    if (data.code === "requires_gender_selection") {
      const userId = data?.user?.id ?? data?.user?.user_id;

      if (userId) {
        localStorage.setItem("user_id", userId);
      }

      window.location = "/Updategender";
      return true;
    }

    if (data.code === "requires_password_change") {
      if (data?.user) {
        localStorage.setItem("userData", JSON.stringify(data.user));
      }

      window.location = "/updatepassword";
      return true;
    }

    return false;
  };

  const handleLoginSuccess = (data) => {
    setError("");
    setCaptchaEnabled(false);
    setCaptchaImage(null);
    setCaptchaId(null);
    setCaptchaResponse("");

    if (data?.two_factor_required === true) {
      setTwoFA(true);
      setLoading(false);
      return;
    }

    saveSession(data);

    if (data?.user?.force_reset_password === 1 || data?.user?.force_reset_password === true) {
      window.location = "/updatepassword";
      return;
    }

    if (data?.user?.role_id === 5 || String(data?.user?.role_id) === "5") {
      if (data?.assessment_required === true) {
        localStorage.setItem("questions", JSON.stringify(data.questions ?? []));
        window.location = "/UserAssessment";
      } else {
        window.location = "/user/dashboard";
      }
      return;
    }

    window.location = "/dashboard";
  };

  const readJsonResponse = async (response) => {
    const contentType = response.headers.get("content-type") || "";

    if (!contentType.includes("application/json")) {
      throw new Error("API returned HTML instead of JSON. Check the API route URL.");
    }

    const data = await response.json();

    if (!response.ok) {
      if (response.status === 405) {
        alert("Session has expired, please login again!");
        window.location = "/login";
        return { handled: true, data };
      }

      applyCaptchaFromResponse(data);

      const handled = handleRequiresAction(data);

      if (!handled) {
        setError(data?.message || "Login failed. Check your credentials.");
      }

      return { handled, data };
    }

    return { handled: false, data };
  };

  const validateTwoFA = async () => {
    const response = await fetch(`${api_url}/verify-2fa`, {
      mode: "cors",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        two_factor_code: twoFaVal,
        login: email,
      }),
    });

    const { handled, data } = await readJsonResponse(response);

    if (!response.ok) {
      setLoading(false);
      return handled;
    }

    handleLoginSuccess(data);
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (captchaEnabled && !captchaResponse) {
        setError("Please complete the CAPTCHA verification.");
        setLoading(false);
        return;
      }

      if (twoFA) {
        await validateTwoFA();
        return;
      }

      const response = await fetch(`${api_url}/login`, {
        mode: "cors",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          login: email,
          password,
          captcha_id: captchaEnabled ? captchaId : null,
          captcha: captchaEnabled ? captchaResponse : null,
        }),
      });

      localStorage.setItem("userEmail", email);

      const { handled, data } = await readJsonResponse(response);

      if (!response.ok) {
        setLoading(false);

        if (!handled && data?.message) {
          throw new Error(data.message);
        }

        return;
      }
      handleLoginSuccess(data);
    } catch (err) {
      setError(err.message || "Login failed. Check your credentials.");
      setLoading(false);
    }
  };

  const loginSettings = async () => {
    try {
      const response = await fetch(`${api_url}/login-info`, {
        mode: "cors",
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Error getting login settings...");
      }

      const result = await response.json();
      const data = result.data ?? {};

      applyLanguage(data.language_code);
      setSystemLanguage(String(data.language_id ?? ""));
      setSystemAllowed(Boolean(data.system_allowed));
      setSSOEnabled(Boolean(data.sso_enabled));

      if (data.captcha_enabled && data.captcha_image) {
        setCaptchaEnabled(true);
        setCaptchaImage(data.captcha_image);
      }
    } catch (err) {
      console.log(err.message);
    }
  };

  const openMicrosoft = async () => {
    setCircleLoading(true);

    try {
      const response = await fetch(`${api_url}/login/microsoft`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Error getting Microsoft login URL...");
      }

      const data = await response.json();

      if (data?.status === "success" && data?.redirect_url) {
        window.location = data.redirect_url;
      }
    } catch (err) {
      console.log(err.message);
    } finally {
      setCircleLoading(false);
    }
  };

  const updateSystemLanguage = async (languageId) => {
    try {
      const response = await fetch(`${api_url}/settings/language`, {
        mode: "cors",
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ language_id: languageId }),
      });

      if (!response.ok) {
        throw new Error("Error updating login language...");
      }

      const data = await response.json();

      if (data.status === "success") {
        loginSettings();
      }
    } catch (err) {
      console.log(err.message);
    }
  };

  useEffect(() => {
    loginSettings();
  }, []);

  return (
    <div className="container">
      <div className="row justify-content-center">
        <div className="col-md-10 col-lg-6">
          <div className="GradientDiv"></div>

          <div className="loginLogo mt-5" align="center">
            <img src={DEFAULT_LOGO_SRC} alt="Cyber Sense" style={{ width: "160px" }} />
          </div>

          <div className="card mt-5 pb-5" style={{ borderRadius: "30px" }}>
            <div className="card-body">
              <h2 className="m-5 txtLoginAccount" align="center">
                {t("Loginaccount")}
              </h2>

              {loadingCircle && (
                <div className="d-flex justify-content-center align-items-center mt-5">
                  <div
                    className="spinner-border loader-api"
                    role="status"
                    style={{
                      borderTopColor: "#6897CB",
                      borderBottomColor: "#6897CB",
                      borderLeftColor: "#6897CB",
                      marginTop: "50px",
                    }}
                  ></div>
                </div>
              )}

              {error && <div className="alert alert-danger mx-5">{error}</div>}

              {systemAllowed ? (
                <div className="px-0 px-md-5">
                  <form onSubmit={handleSubmit} className="logoForm">
                    <div className="form-group mb-3">
                      <label htmlFor="email">{t("EmailUsername")}</label>
                      <input
                        type="text"
                        className="form-control"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>

                    {!twoFA && (
                      <div className="form-group mb-3">
                        <label htmlFor="password">{t("Password")}</label>
                        <input
                          type="password"
                          className="form-control"
                          id="password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                        />
                      </div>
                    )}

                    {!twoFA && (
                      <div className="mt-2 text-end mb-3">
                        <a href="/forgot-password" className="text-primary">
                          {t("ForgotPassword?")}
                        </a>
                      </div>
                    )}

                    {captchaImage && (
                      <div className="mb-3">
                        <label htmlFor="captcha">{t("EnterCAPTCHA")}</label>
                        <div>
                          <img src={captchaImage} alt="CAPTCHA" className="captcha-image" />
                        </div>
                        <input
                          type="text"
                          className="form-control mt-2"
                          id="captcha"
                          value={captchaResponse}
                          onChange={(e) => setCaptchaResponse(e.target.value)}
                          required
                        />
                      </div>
                    )}

                    {twoFA && (
                      <div className="mb-3">
                        <label htmlFor="twoFA">{t("Enter2FACode")}</label>
                        <input
                          type="text"
                          className="form-control mt-2"
                          id="twoFA"
                          value={twoFaVal}
                          onChange={(e) => setTwoFaVal(e.target.value)}
                          required
                        />
                      </div>
                    )}

                    <button type="submit" className="btn btn-dark w-100 p-2" disabled={loading}>
                      {loading ? (
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                      ) : twoFA ? (
                        t("Verify")
                      ) : (
                        t("LoginAccount")
                      )}
                    </button>
                  </form>

                  <br />

                  {SSOEnabled && !twoFA && (
                    <div>
                      <div className="line-text">
                        <div className="line"></div>
                        <span className="text">{t("OR")}</span>
                        <div className="line"></div>
                      </div>

                      <div className="loginMicrosoft mt-3 d-flex justify-content-center" onClick={openMicrosoft}>
                        <img src="/images/signupmicrosoft.png" alt="Signup with Microsoft 365" />
                      </div>
                    </div>
                  )}

                  <div className="py-3">
                    <select
                      className="form-select"
                      value={systemLanguage}
                      onChange={(e) => updateSystemLanguage(e.target.value)}
                    >
                      <option value="1">{t("English")}</option>
                      <option value="2">{t("العربی")}</option>
                    </select>
                  </div>
                </div>
              ) : (
                <div className="alert alert-danger">{t("SystemLicenseExpired,PleaseContactSystemAdmin")}</div>
              )}
            </div>
          </div>

          <div className="mt-3 mb-3 text-aha text-center">{t("©2025Allrightsreserved.")}</div>
        </div>
      </div>
    </div>
  );
}

export default Login;